<template>
  <v-card class="me-8 mb-2 translucent-background area-details-fixed"  v-if="show">
    <v-card-title class="d-flex justify-space-between">
      <div class="d-flex align-end">
        <div class="me-3">
          <!--v-icon :class="'me-3 text-' + acceptanceColor()">{{ acceptanceIcon() }}</v-icon-->
          <div class="area-id">#{{ area.id }}</div>
        </div>
        {{ area.address.street }}
      </div>
      <v-btn density="comfortable" elevation="0" icon="mdi-close" @click="close"></v-btn>
    </v-card-title>

    <v-card-text class="area-details-content">
      <div class="d-flex ga-2">
        <div class="">
          <v-img :src="area.clipped_topview.image_url" width="auto" height="15rem" max-width="40rem" max-height="15rem" />
        </div>

        <div class="area-details-metrics">
          <div class="area-details-section-title">{{ area.address.city }}, {{ round(area.area_sqm) }} m²</div>

          <div class="area-details-section-title">Akzeptanzkriterien</div>
          <div v-for="criterion in sortedAcceptanceCriteria" :key="criterion.name + criterion.category" class="area-details-section-text" :title="JSON.stringify(criterion, null, 2)">
            <v-icon :class="'me-3 text-' + acceptanceColor(criterion.accepted)">{{ acceptanceIcon(criterion.accepted) }}</v-icon>{{ criterion.name }}
          </div>

          <div class="area-details-section-title">Score <span class="ms-2 area-details-section-title-score-value">{{ round(area.priority_score, 3) }}</span></div>
          <div v-for="score in sortedScores" :key="score.name" class="area-details-section-text" :title="JSON.stringify(score, null, 2)">
            {{ score.name }}: {{ round(score.score, 3) }}
          </div>

          <div class="area-details-section-title">Kontakte</div>
          <div v-if="contacts.length === 0" class="d-flex align-center ga-2 mb-2">
            <v-btn
              color="primary"
              density="comfortable"
              prepend-icon="mdi-account-search"
              :loading="contactsLoading"
              :disabled="contactsLoading || !area.can?.update"
              @click="enqueueContactsSearch"
            >
              Kontakte ermitteln
            </v-btn>
          </div>
          <div v-if="contacts.length === 0" class="area-details-section-text">Keine Kontaktdaten im Radius gefunden.</div>
          <div
            v-for="contact in contacts"
            :key="contact.id"
            :id="`area-contact-item-${contact.id}`"
            :class="['contact-item', { 'is-selected': selectedContactId === contact.id }]"
            @click="selectContact(contact)"
          >
            <div class="contact-name">
              <a v-if="contact.homepage" :href="contact.homepage" target="_blank" rel="noopener">
                {{ contact.name }}
              </a>
              <span v-else>{{ contact.name }}</span>
            </div>
            <div v-if="contact.email" class="area-details-section-text">
              <a :href="`mailto:${contact.email}`">{{ contact.email }}</a>
            </div>
            <div v-if="contact.phone_number" class="area-details-section-text">
              <a :href="`tel:${contact.phone_number}`">{{ contact.phone_number }}</a>
            </div>
            <div v-if="contact.google_maps_url" class="area-details-section-text">
              <a :href="contact.google_maps_url" target="_blank" rel="noopener">Auf Google Maps...</a>
            </div>
          </div>
        </div>
      </div>
    </v-card-text>

    <v-card-actions class="d-flex justify-end">
      <v-btn color="primary" prepend-icon="mdi-google-maps" target="_blank" :href="area.google_maps_url">Google Maps...</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup>
  import { computed, ref, watch, nextTick } from "vue";
  import { useCommandStore } from "../../stores/command";
  import { useFlashStore } from "../../stores/flash";
  import { round } from "../../services/number-helpers";

  const area = defineModel();
  const selectedContactId = defineModel("selectedContactId");
  const command = useCommandStore();
  const flash = useFlashStore();
  const contactsLoading = ref(false);
  const show = computed(() => area.value != null);
  const contacts = computed(() => area.value?.contacts || []);

  const sortedAcceptanceCriteria = computed(() => {
    return (area.value?.acceptance_criteria || []).slice().sort((a, b) => a.name.localeCompare(b.name));
  });
  const sortedScores = computed(() => {
    return (area.value?.analysis || [])
      .filter(score => Number.isFinite(Number(score.score)))
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  watch(() => area.value, () => {
    contactsLoading.value = false;
  });

  function acceptanceColor(accepted=null) {
    accepted ??= area.value.accepted;
    return accepted ? "success" : "error";
  }

  function acceptanceIcon(accepted=null) {
    accepted ??= area.value.accepted;
    return accepted ? "mdi-check" : "mdi-close";
  }

  function close() {
    selectedContactId.value = null;
    area.value = null;
  }

  function selectContact(contact) {
    selectedContactId.value = contact.id;
  }

  async function enqueueContactsSearch() {
    if (!area.value?.id || contactsLoading.value) return;

    contactsLoading.value = true;
    const data = await command.execute("Reporting::Area", "enqueue_contacts_job", { id: area.value.id });
    if (!data.success) {
      data.errors.forEach(flash.error);
    }
  }

  watch(() => selectedContactId.value, async (contactId) => {
    if (!contactId) return;

    await nextTick();
    const element = document.getElementById(`area-contact-item-${contactId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  });
</script>

<style scoped>
  .area-id {
    font-size: 0.7rem;
  }

  .area-details-section-title {
    margin-top: 0.9rem;
    margin-bottom: 0.2rem;
    font-size: 1rem;
  }

  .area-details-section-text {
    font-size: 0.8rem;
  }

  .area-details-section-title-score-value {
    font-size: 0.8rem;
  }

  .contact-item {
    padding: 0.4rem;
    border-radius: 0.3rem;
    margin-bottom: 0.4rem;
    cursor: pointer;
    border: 1px solid transparent;
  }

  .contact-item:hover {
    border-color: #90caf9;
    background-color: rgba(33, 150, 243, 0.08);
  }

  .contact-item.is-selected {
    border-color: #1976d2;
    background-color: rgba(25, 118, 210, 0.12);
  }

  .contact-name {
    font-size: 0.85rem;
    font-weight: 600;
  }

  .img-container {
    height: 20rem;
    max-height: 20rem;
    width: auto;
    max-width: 50%;
  }

  .translucent-background {
    background-color: rgba(255, 255, 255, 0.95);
  }

  .area-details-fixed {
    position: absolute;
    bottom: 0;
    right: 0;
    height: 32rem;
    max-height: 32rem;
    max-width: 75vw;
    display: flex;
    flex-direction: column;
  }

  .area-details-content {
    flex: 1 1 auto;
    min-height: 0;
  }

  .area-details-metrics {
    min-width: 20rem;
    max-width: 20rem;
    width: 20rem;
    overflow-y: auto;
    max-height: 24rem;
  }
</style>
